/**
 * The "Updating…" splash that masks a full-document reload triggered by an app
 * update, so swapping to the new build reads as one continuous loading state
 * rather than a jarring flash of the app disappearing and reappearing.
 *
 * It has two halves that share one visual:
 *  - Outgoing: showUpdatingOverlay() reveals the splash the instant the user
 *    acts, and it stays up through teardown until the reload navigates away.
 *  - Incoming: an inline script in index.html re-reveals the same #update-splash
 *    element on the new document before React boots (so there is no white flash
 *    of an empty #root), and dismissUpdatingOverlay() fades it out once the app
 *    has painted underneath.
 *
 * The flag in sessionStorage carries the "this reload is an update" intent
 * across the navigation. Its key MUST stay in sync with the literal used by the
 * inline reveal script in index.html.
 */

const UPDATING_FLAG = 'paygcrm:updating';
const SPLASH_ID = 'update-splash';
const FADE_MS = 240;

function readUpdatingFlag(): boolean {
  try {
    return sessionStorage.getItem(UPDATING_FLAG) === '1';
  } catch {
    return false;
  }
}

function markUpdating(): void {
  try {
    sessionStorage.setItem(UPDATING_FLAG, '1');
  } catch {
    // Private mode / storage disabled — the overlay is a nicety, not required.
  }
}

function clearUpdatingFlag(): void {
  try {
    sessionStorage.removeItem(UPDATING_FLAG);
  } catch {
    // Ignore — a stale flag only affects the next reload's cosmetics.
  }
}

/**
 * Builds a self-styled splash for builds that predate the index.html markup
 * (their document has neither the #update-splash element nor its inline CSS).
 * Three pulsing primary dots so it looks like the app's own loading state
 * without depending on the app stylesheet.
 */
function createFallbackSplash(): HTMLElement {
  const isDark = document.documentElement.classList.contains('dark');
  const background = isDark ? '#1a202c' : '#ffffff';
  const dotColor = isDark ? '#60a5fa' : '#0068ff';

  if (!document.getElementById('update-splash-style')) {
    const style = document.createElement('style');
    style.id = 'update-splash-style';
    style.textContent =
      '@keyframes update-splash-pulse{0%,100%{opacity:1}50%{opacity:.5}}' +
      '@media (prefers-reduced-motion:reduce){#update-splash .update-splash__dot{animation:none}}';
    document.head.appendChild(style);
  }

  const splash = document.createElement('div');
  splash.id = SPLASH_ID;
  splash.setAttribute('role', 'status');
  splash.setAttribute('aria-label', 'Updating');
  splash.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    `background:${background}`,
    `transition:opacity ${FADE_MS}ms ease`,
  ].join(';');

  const dots = document.createElement('div');
  dots.style.cssText = 'display:flex;align-items:center;gap:6px';
  for (let index = 0; index < 3; index += 1) {
    const dot = document.createElement('span');
    dot.className = 'update-splash__dot';
    dot.style.cssText = [
      'width:8px',
      'height:8px',
      'border-radius:9999px',
      `background:${dotColor}`,
      `animation:update-splash-pulse 1s cubic-bezier(0.4,0,0.6,1) ${index * 150}ms infinite`,
    ].join(';');
    dots.appendChild(dot);
  }
  splash.appendChild(dots);
  return splash;
}

/**
 * Reveal the "Updating…" splash and record that the imminent reload is an
 * update, so the new document re-shows it before React boots.
 */
export function showUpdatingOverlay(): void {
  if (typeof document === 'undefined') {
    return;
  }
  markUpdating();

  let splash = document.getElementById(SPLASH_ID);
  if (!splash) {
    splash = createFallbackSplash();
    document.body.appendChild(splash);
  }
  splash.classList.remove('is-leaving');
  splash.classList.add('is-visible');
}

/**
 * Fade the splash out to reveal the freshly-booted app, then remove it from the
 * layout. No-op unless this load was an update reload and the splash is showing.
 */
export function dismissUpdatingOverlay(): void {
  if (typeof document === 'undefined') {
    return;
  }
  const wasUpdate = readUpdatingFlag();
  clearUpdatingFlag();

  const splash = document.getElementById(SPLASH_ID);
  if (!splash?.classList.contains('is-visible')) {
    return;
  }
  if (!wasUpdate) {
    // Showing without our flag shouldn't happen; hide immediately rather than
    // animate an unexplained overlay.
    splash.classList.remove('is-visible', 'is-leaving');
    return;
  }

  // Fade, then drop out of the flow. transitionend covers the normal case; the
  // timeout covers reduced-motion (no transition fires) and any dropped event.
  const cleanup = (): void => {
    splash.classList.remove('is-visible', 'is-leaving');
  };
  splash.addEventListener('transitionend', cleanup, { once: true });
  splash.classList.add('is-leaving');
  window.setTimeout(cleanup, FADE_MS + 120);
}

/**
 * Remove the cache-busting query param a force reload adds, so the user lands on
 * a clean URL. Call before the router reads the location.
 */
export function stripCacheBustParam(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('_cache_bust')) {
      return;
    }
    url.searchParams.delete('_cache_bust');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // Non-fatal — a lingering param is cosmetic only.
  }
}
